import keycloakApi from './keycloak-auth';

export type RealmRole = 'internal' | 'admin' | 'basic';

export interface CreateUserRequest {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  password: string;
  partyId: string;
  roles: RealmRole[];
  /** Provision SFTP access during creation, so no follow-up edit is needed. */
  isSftp?: boolean;
  sftpUsername?: string | null;
  keycloakGroup?: string | null;
  sshPublicKeys?: string[];
}

export interface CreateUserResponse {
  id: string;
  keycloakUserId: string;
  email: string;
  firstName: string;
  lastName: string;
  partyId: string;
  roles: RealmRole[];
  isSftp: boolean;
  keycloakGroup: string | null;
  sshPublicKeys: string[];
}

const apiCall = async <T>(endpoint: string, options: RequestInit = {}): Promise<T> => {
  return keycloakApi.authenticatedApiCall<T>(endpoint, options);
};

export const createUser = async (data: CreateUserRequest): Promise<CreateUserResponse> => {
  return apiCall<CreateUserResponse>('/users', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export interface KeycloakUser {
  id: string;
  username: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  enabled: boolean;
  emailVerified: boolean;
  createdTimestamp: number;
  roles: string[];
  partyId?: string | null;
  clientName?: string | null;
}

export interface UsersResponse {
  data: KeycloakUser[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export interface UserFilters {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  order?: 'asc' | 'desc';
}

export interface UserDetail extends KeycloakUser {
  phoneNumber?: string;
  createdAt?: string;
  updatedAt?: string;
  isSftp?: boolean;
  sftpUsername?: string | null;
  keycloakGroup?: string | null;
  sshPublicKeys?: string[];
}

export interface UpdateUserRequest {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string | null;
  partyId?: string;
  roles?: RealmRole[];
  enabled?: boolean;
  isSftp?: boolean;
  sftpUsername?: string | null;
  keycloakGroup?: string | null;
  /** Omit to leave stored keys untouched; [] clears them. */
  sshPublicKeys?: string[];
}

export interface UpdateUserResponse {
  id: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  enabled: boolean;
  roles: string[];
  partyId: string | null;
  clientName: string | null;
  phoneNumber?: string;
  isSftp: boolean;
  keycloakGroup: string | null;
  sshPublicKeys: string[];
}

export const getUserById = async (id: string): Promise<UserDetail> => {
  return apiCall<UserDetail>(`/users/${id}`);
};

export const updateUser = async (
  id: string,
  data: UpdateUserRequest
): Promise<UpdateUserResponse> => {
  return apiCall<UpdateUserResponse>(`/users/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
};

export const getUsers = async (filters: UserFilters = {}): Promise<UsersResponse> => {
  const searchParams = new URLSearchParams();

  if (filters.page) searchParams.append('page', filters.page.toString());
  if (filters.limit) searchParams.append('limit', filters.limit.toString());
  if (filters.search) searchParams.append('search', filters.search);
  if (filters.sortBy) searchParams.append('sortBy', filters.sortBy);
  if (filters.order) searchParams.append('sortOrder', filters.order.toUpperCase());

  const queryString = searchParams.toString();
  const endpoint = `/users${queryString ? `?${queryString}` : ''}`;

  return apiCall<UsersResponse>(endpoint);
};
